/**
 * Cross-sell / upsell recommendations for the cart drawer.
 * Recalculates on every cart change based on CART TOTAL (price band),
 * then breaks ties by category/tag overlap with the most recently added item.
 * Built for Plan Toys Mexico - draft theme "Draft - FBT cross-sell 7sep2026".
 */
(function () {
  'use strict';

  var CATALOG_URL = window.crossSellCatalogUrl;
  var CONTAINER_ID = 'CrossSellRecommendations';
  var STORAGE_PREV_CART = 'ptCrossSellPrevCart';
  var STORAGE_SHOWN = 'ptCrossSellShown';
  var SHOWN_HISTORY_LIMIT = 15;
  var SUGGESTION_COUNT = 3;

  // Cart-total -> suggestion price range. Approved 2026-09 with Adriana.
  var PRICE_BANDS = [
    { min: 0, max: 600, sMin: 600, sMax: 900 },
    { min: 600, max: 1200, sMin: 350, sMax: 650 },
    { min: 1200, max: 2200, sMin: 300, sMax: 650 },
    { min: 2200, max: 3500, sMin: 300, sMax: 550 },
    { min: 3500, max: Infinity, sMin: 150, sMax: 400 },
  ];

  var HEADLINES = {
    dollhouse: ['Completa la casita', 'Ideal para amueblar su casita'],
    kitchen: ['Arma la cocina completa', 'Para seguir cocinando'],
    roleplay: ['Sigue el juego de roles', 'Complementa su set'],
    construction: ['Sigue construyendo', 'Para seguir armando'],
    music: ['Completa la banda musical', 'Para seguir haciendo ruido'],
    waterplay: ['Para el momento del agua', 'Sigue jugando con agua'],
    vehicles: ['Sigue la flotilla', 'Para completar el garage'],
    city_world: ['Sigue construyendo su mundo', 'Amplía su ciudad'],
    learning: ['Sigue aprendiendo jugando', 'Para seguir descubriendo'],
    babies: ['Ideal para su bebé', 'Sigue estimulando sus sentidos'],
    active: ['Para seguir moviéndose', 'Más juego activo'],
    games: ['Suma un juego más', 'Para seguir jugando en familia'],
    home: ['Para el rincón de juego', 'Complementa su espacio'],
    _default: ['Ya que elegiste esto, también le puede gustar', 'Completa la experiencia de juego'],
  };

  var catalogPromise = null;
  var container = null;
  var observer = null;
  var renderToken = 0;

  function fetchJSON(url, opts) {
    return fetch(url, opts).then(function (r) {
      if (!r.ok) throw new Error('Request failed: ' + url);
      return r.json();
    });
  }

  function getCatalog() {
    if (!catalogPromise) {
      catalogPromise = CATALOG_URL
        ? fetchJSON(CATALOG_URL)
        : Promise.resolve([]);
    }
    return catalogPromise;
  }

  function getCart() {
    return fetchJSON('/cart.js');
  }

  function readStorage(key) {
    try {
      var raw = sessionStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function writeStorage(key, value) {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      /* storage unavailable, degrade silently */
    }
  }

  function bandFor(total) {
    for (var i = 0; i < PRICE_BANDS.length; i++) {
      var b = PRICE_BANDS[i];
      if (total >= b.min && total < b.max) return b;
    }
    return PRICE_BANDS[PRICE_BANDS.length - 1];
  }

  // Figures out which product was most likely just added, by diffing
  // the current cart against the last snapshot we stored.
  function detectReferenceProductId(cart) {
    var prev = readStorage(STORAGE_PREV_CART) || {};
    var current = {};
    cart.items.forEach(function (item) {
      current[item.product_id] = (current[item.product_id] || 0) + item.quantity;
    });

    var candidate = null;
    cart.items.forEach(function (item) {
      var prevQty = prev[item.product_id] || 0;
      if (item.quantity > prevQty) candidate = item.product_id;
    });

    writeStorage(STORAGE_PREV_CART, current);

    if (candidate) return String(candidate);
    if (cart.items.length) return String(cart.items[cart.items.length - 1].product_id);
    return null;
  }

  function catsFor(catalog, productId) {
    var entry = catalog.filter(function (p) {
      return p.id === productId;
    })[0];
    return entry ? entry.cats : [];
  }

  function pickHeadline(cats) {
    var key = cats && cats.length ? cats[0] : '_default';
    var options = HEADLINES[key] || HEADLINES._default;
    return options[Math.floor(Math.random() * options.length)];
  }

  function chooseRecommendations(catalog, cart) {
    var band = bandFor(cart.total_price / 100);
    var cartProductIds = {};
    cart.items.forEach(function (item) {
      cartProductIds[String(item.product_id)] = true;
    });

    var referenceId = detectReferenceProductId(cart);
    var referenceCats = referenceId ? catsFor(catalog, referenceId) : [];

    var shownHistory = readStorage(STORAGE_SHOWN) || [];

    var eligible = catalog.filter(function (p) {
      return (
        p.price >= band.sMin &&
        p.price <= band.sMax &&
        !cartProductIds[p.id]
      );
    });

    function overlapScore(p) {
      if (!referenceCats.length) return 0;
      var score = 0;
      p.cats.forEach(function (c) {
        if (referenceCats.indexOf(c) !== -1) score++;
      });
      return score;
    }

    eligible.forEach(function (p) {
      p._score = overlapScore(p);
      p._recentlyShown = shownHistory.indexOf(p.id) !== -1;
    });

    // Prefer: higher category overlap, then not-recently-shown, then a
    // stable-ish shuffle so repeated visits in the same band still vary.
    eligible.sort(function (a, b) {
      if (b._score !== a._score) return b._score - a._score;
      if (a._recentlyShown !== b._recentlyShown) return a._recentlyShown ? 1 : -1;
      return Math.random() - 0.5;
    });

    var chosen = eligible.slice(0, SUGGESTION_COUNT);

    // If the band is thin and everything eligible was already shown, fall
    // back to allowing repeats rather than showing fewer than 3.
    if (chosen.length < SUGGESTION_COUNT) {
      var already = {};
      chosen.forEach(function (p) {
        already[p.id] = true;
      });
      catalog
        .filter(function (p) {
          return (
            p.price >= band.sMin &&
            p.price <= band.sMax &&
            !cartProductIds[p.id] &&
            !already[p.id]
          );
        })
        .forEach(function (p) {
          if (chosen.length < SUGGESTION_COUNT) chosen.push(p);
        });
    }

    var newHistory = shownHistory
      .concat(chosen.map(function (p) { return p.id; }))
      .slice(-SHOWN_HISTORY_LIMIT);
    writeStorage(STORAGE_SHOWN, newHistory);

    return { chosen: chosen, headline: pickHeadline(referenceCats) };
  }

  function moneyFormat(amountCents) {
    try {
      return (amountCents / 100).toLocaleString('es-MX', {
        style: 'currency',
        currency: window.Shopify && window.Shopify.currency ? window.Shopify.currency.active : 'MXN',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    } catch (e) {
      return '$' + (amountCents / 100).toFixed(2);
    }
  }

  // Catalog prices are plain currency units (e.g. 679.0), not cents.
  // Reuse moneyFormat's cents-based formatting by scaling up first, rather
  // than duplicating the Intl/currency logic.
  function moneyFormatFromUnits(amount) {
    return moneyFormat(amount * 100);
  }

  function cardMarkup(product) {
    var imageHtml = product.image
      ? '<img class="cross-sell-card__image" alt="" loading="lazy" src="' + product.image + '">'
      : '<div class="cross-sell-card__image-placeholder"></div>';
    return (
      '<div class="cross-sell-card" data-cross-sell-handle="' + product.handle + '">' +
      '<a class="cross-sell-card__link" href="/products/' + product.handle + '">' +
      '<div class="cross-sell-card__image-wrap">' +
      imageHtml +
      '</div>' +
      '<p class="cross-sell-card__title">' + product.title + '</p>' +
      '<p class="cross-sell-card__price">' + moneyFormatFromUnits(product.price) + '</p>' +
      '</a>' +
      '<button type="button" class="cross-sell-card__add button button--tertiary">' +
      'Añadir' +
      '</button>' +
      '</div>'
    );
  }

  // Runs only when the shopper actually clicks "Añadir" - fetches the live
  // product data (to get a real, currently-available variant id) and adds
  // it to the cart. This replaces the old hydrateCard() which used to run
  // this fetch automatically for every card on every render.
  function handleAddClick(product, buttonEl) {
    buttonEl.disabled = true;
    var originalText = buttonEl.textContent;
    buttonEl.textContent = 'Añadiendo...';

    fetchJSON('/products/' + product.handle + '.js')
      .then(function (fullProduct) {
        var variant = fullProduct.variants.filter(function (v) { return v.available; })[0] || fullProduct.variants[0];
        if (!variant || !variant.available) {
          buttonEl.textContent = 'Agotado';
          return;
        }
        addToCart(variant.id, buttonEl, originalText);
      })
      .catch(function () {
        buttonEl.disabled = false;
        buttonEl.textContent = originalText;
      });
  }

  function addToCart(variantId, buttonEl, originalText) {
    fetchJSON('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: 'cart-drawer,cart-icon-bubble',
        sections_url: window.location.pathname,
      }),
    })
      .then(function (parsedState) {
        var drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.renderContents === 'function') {
          drawer.renderContents(parsedState);
        }
        buttonEl.textContent = 'Añadido';
      })
      .catch(function () {
        buttonEl.disabled = false;
        buttonEl.textContent = originalText;
      });
  }

  function render(catalog, cart) {
    container = document.getElementById(CONTAINER_ID);
    if (!container) return;

    if (!cart.items.length) {
      container.innerHTML = '';
      return;
    }

    var result = chooseRecommendations(catalog, cart);
    if (!result.chosen.length) {
      container.innerHTML = '';
      return;
    }

    var myToken = ++renderToken;

    var html =
      '<div class="cross-sell-recommendations__inner">' +
      '<p class="cross-sell-recommendations__headline">' + result.headline + '</p>' +
      '<div class="cross-sell-recommendations__grid">' +
      result.chosen.map(cardMarkup).join('') +
      '</div>' +
      '</div>';
    container.innerHTML = html;

    result.chosen.forEach(function (product) {
      if (myToken !== renderToken) return;
      var cardEl = container.querySelector('[data-cross-sell-handle="' + product.handle + '"]');
      if (!cardEl) return;
      var addBtn = cardEl.querySelector('.cross-sell-card__add');
      if (addBtn) {
        addBtn.addEventListener('click', function () {
          handleAddClick(product, addBtn);
        });
      }
    });
  }

  function update() {
    Promise.all([getCatalog(), getCart()])
      .then(function (results) {
        render(results[0], results[1]);
      })
      .catch(function () {
        /* fail silently, never block the cart drawer */
      });
  }

  var debounceTimer = null;
  function scheduleUpdate() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(update, 120);
  }

  function observeDrawer() {
    var drawerRoot = document.getElementById('CartDrawer');
    if (!drawerRoot || observer) return;
    observer = new MutationObserver(function (mutations) {
      // Ignore mutations we caused ourselves (inside our own container).
      var relevant = mutations.some(function (m) {
        return !container || !container.contains(m.target);
      });
      if (relevant) scheduleUpdate();
    });
    observer.observe(drawerRoot, { childList: true, subtree: true });
  }

  function init() {
    observeDrawer();
    update();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
